import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "../layouts/PageTitleContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faClipboardList,
    faArrowUp, faArrowDown,
    faBoxOpen, faArrowsRotate, faCalendarDays, faHourglassHalf, faCircleCheck, faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { Joyride, STATUS } from "react-joyride";
import { NavLink } from "react-router-dom";
import ErrorState from "../components/ErrorState";
import SalesTrendChart from "../components/Charts/SalesTrendChart";
import { useAuth } from "../context/AuthProvider";
import { API_BASE_URL } from "../api";
import "./Dashboard.scss";

const stringToColor = (str = "") => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    let color = "#";
    for (let i = 0; i < 3; i++) color += (`00${((hash >> (i * 8)) & 0xFF).toString(16)}`).slice(-2);
    return color;
};

const nameInitials = (name = "") => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

// Pastel tile + dark text of the same hue, picked from the name — stays readable whatever the
// hue (stringToColor's raw colors can be too light for white initials)
const avatarStyle = (name = "") => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const hue = Math.abs(hash) % 360;
    return { background: `hsl(${hue}, 70%, 93%)`, color: `hsl(${hue}, 55%, 35%)` };
};

/* ─── Status Pill ─────────────────────────────────── */
const Pill = ({ status }) => {
    const { t } = useTranslation();
    const map = {
        PENDING:    { label: t("Pending"),            cls: "pill-amber" },
        PROCESSING: { label: t("dashboard.processing"), cls: "pill-blue"  },
        COMPLETE:   { label: t("Complete"),           cls: "pill-green" },
        NEW:        { label: t("New"),                cls: "pill-blue"  },
        REGULAR:    { label: t("dashboard.regular"),   cls: "pill-neutral"},
    };
    const cfg = map[status?.toUpperCase()] ?? { label: status, cls: "pill-neutral" };
    return <span className={`status-pill ${cfg.cls}`}>{cfg.label}</span>;
};

/* ─── Loading placeholders (shimmer bars, .skel from Listing/Table.scss) ─── */
// Activité feed rows: icon tile + two lines + amount
const FeedSkeleton = ({ rows = 5 }) => (
    <div className="feed" aria-hidden="true">
        {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="feed-row">
                <span className="skel dash-skel-icon" />
                <div className="feed-body">
                    <span className="skel skel--line" style={{ width: `${50 + ((i * 17) % 30)}%` }} />
                    <span className="skel skel--line skel--sm" style={{ width: `${30 + ((i * 11) % 20)}%` }} />
                </div>
                <span className="skel skel--line" style={{ width: 64 }} />
            </div>
        ))}
    </div>
);

// First load: the page's own layout in gray — period controls, KPI cards, chart | to do
const DashboardSkeleton = () => (
    <div className="page-content dash" aria-busy="true">
        <div className="dash-toolbar">
            <span className="skel dash-skel-periods" />
        </div>
        <div className="kpi-grid">
            {[0, 1, 2, 3].map((i) => (
                <div key={i} className="kpi-card kpi-card--static">
                    <span className="skel skel--line" style={{ width: '45%', height: 10 }} />
                    <span className="skel skel--line dash-skel-value" />
                    <span className="skel skel--line" style={{ width: '55%', height: 10 }} />
                    <span className="skel kpi-card__spark" />
                </div>
            ))}
        </div>
        <div className="dash-row">
            <div className="dash-skel-section">
                <span className="skel skel--line" style={{ width: 150 }} />
                <span className="skel skel--line skel--sm" style={{ width: 320 }} />
                {[0, 1, 2, 3, 4].map((i) => (
                    <span key={i} className="skel skel--line dash-skel-row" style={{ width: `${70 + ((i * 13) % 25)}%` }} />
                ))}
            </div>
            <div className="dash-skel-section dash-skel-section--side">
                {/* tabs, then the rows (the Activité panel has no header) */}
                <span className="skel" style={{ height: 36, borderRadius: 8 }} />
                <FeedSkeleton />
            </div>
        </div>
    </div>
);

// Small area chart of a metric over the period's days, at the bottom of its KPI card. Stretched
// to the card's width (preserveAspectRatio none; non-scaling stroke keeps the line 1.5px).
const Spark = ({ values, active }) => {
    const gradId = `spark-${React.useId()}`;
    if (values.length < 2) return <span className="kpi-card__spark kpi-card__spark--empty" />;
    const w = 100, h = 32;
    const max = Math.max(...values) || 1;
    const pts = values.map((v, i) => `${((i / (values.length - 1)) * w).toFixed(2)},${(h - 2 - (v / max) * (h - 6)).toFixed(2)}`).join(" ");
    const color = active ? "#2563eb" : "#94a3b8";
    return (
        <svg className="kpi-card__spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
            <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.18" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#${gradId})`} />
            <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
    );
};

// KPI card: label + trend chip, the figure, a context line and the period's sparkline. A button
// that switches the "Évolution" chart to this metric (selected: blue outline) — or a plain card
// (onSelect absent) for a figure with no history, which shows an action link instead.
// unit: small and gray after the value, as money is everywhere else (e.g. "dhs")
// tag: small chip before the context line — "Actuel" on a figure that doesn't follow the period
const KpiCard = ({ label, value, unit, sub, delta, tag, spark, link, loading, active, onSelect, t }) => {
    const Tag = onSelect ? "button" : "div";
    return (
        <Tag
            {...(onSelect ? { type: "button", onClick: onSelect, "aria-pressed": active } : {})}
            className={`kpi-card ${active ? "is-active" : ""} ${onSelect ? "" : "kpi-card--static"}`}
        >
            <span className="kpi-card__top">
                <span className="kpi-card__label">{label}</span>
                {!loading && delta && (
                    <span className={`kpi-delta kpi-delta--${delta.dir}`} title={t('dashboard.vs_previous')}>
                        <FontAwesomeIcon icon={delta.dir === "down" ? faArrowDown : faArrowUp} style={{ fontSize: 9 }} />
                        {delta.text}
                    </span>
                )}
            </span>
            {loading ? (
                <>
                    <span className="skel skel--line dash-skel-value" />
                    <span className="skel skel--line" style={{ width: '55%', height: 10 }} />
                </>
            ) : (
                <>
                    <span className="kpi-card__value">
                        {value}
                        {unit && <span className="kpi-card__unit">{unit}</span>}
                    </span>
                    <span className="kpi-card__sub">
                        {tag && <span className="kpi-tag">{tag}</span>}
                        {sub && <span className="kpi-sub" title={sub}>{sub}</span>}
                    </span>
                </>
            )}
            {spark ? <Spark values={loading ? [] : spark} active={active} /> : link}
        </Tag>
    );
};

const fmt = (val) =>
    new Intl.NumberFormat("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

const PERIOD_DAYS = [1, 7, 30];
const FEED_PAGE_SIZE = 5;
const PRODS_PAGE_SIZE = 5;

// `days` is either a rolling window ending today (number) or "yesterday"; a custom range
// is handled separately (period === "custom" + customRange) since it has no fixed button.
const getPeriods = (t) => [
    { label: t("dashboard.today"), days: 1 },
    { label: t("dashboard.yesterday"), days: "yesterday" },
    { label: t("dashboard.period_7d"), days: 7 },
    { label: t("dashboard.period_30d"), days: 30 },
];

const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// The bookmark stores a number of days, "yesterday", or "custom:YYYY-MM-DD:YYYY-MM-DD".
const parsePeriodBookmark = (raw) => {
    if (raw === "yesterday") return { period: "yesterday" };
    if (typeof raw === "string" && raw.startsWith("custom:")) {
        const [, from, to] = raw.split(":");
        if (ISO_DATE_RE.test(from) && ISO_DATE_RE.test(to) && from <= to) return { period: "custom", customRange: { from, to } };
        return null;
    }
    const days = Number(raw);
    return PERIOD_DAYS.includes(days) ? { period: days } : null;
};

const getPeriodRange = (period, customRange) => {
    if (period === "custom") return customRange;
    const to = new Date();
    if (period === "yesterday") {
        to.setDate(to.getDate() - 1);
        return { from: toISODate(to), to: toISODate(to) };
    }
    const from = new Date();
    from.setDate(from.getDate() - (period - 1));
    return { from: toISODate(from), to: toISODate(to) };
};

// The same-length stretch right before { from, to } — what the KPI deltas compare against
const getPreviousRange = ({ from, to }) => {
    const start = new Date(`${from}T00:00:00`);
    const end = new Date(`${to}T00:00:00`);
    const days = Math.round((end - start) / 86400000) + 1;
    const prevTo = new Date(start);
    prevTo.setDate(prevTo.getDate() - 1);
    const prevFrom = new Date(prevTo);
    prevFrom.setDate(prevFrom.getDate() - (days - 1));
    return { from: toISODate(prevFrom), to: toISODate(prevTo) };
};

// Change vs the previous period, as { text: "12 %", dir: "up" | "down" } — null when there's
// nothing meaningful to compare with (no previous data, or no change)
const computeDelta = (current, previous) => {
    const cur = Number(current) || 0;
    const prev = Number(previous) || 0;
    if (prev === 0 || cur === prev) return null;
    const pct = ((cur - prev) / prev) * 100;
    const dir = pct > 0 ? "up" : "down";
    // a near-empty previous period gives meaningless figures (↑ 10164 %) — no chip at all
    if (Math.abs(pct) > 999) return null;
    const rounded = Math.abs(pct) >= 10 ? Math.round(Math.abs(pct)) : Math.round(Math.abs(pct) * 10) / 10;
    return { text: `${String(rounded).replace('.', ',')} %`, dir };
};

const AUTO_REFRESH_MS = 5 * 60 * 1000;

const formatShortDate = (iso) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString("fr-MA", { day: "numeric", month: "short", year: "numeric" });

// The actual dates of the selected period, next to the period buttons: "Du 25 oct. au 31 oct. 2026",
// or "samedi 31 octobre 2026" for a single day (year on the start date only when it differs)
const formatRangeLabel = ({ from, to }, t) => {
    const date = (iso, opts) => new Date(`${iso}T00:00:00`).toLocaleDateString("fr-MA", opts);
    if (from === to) return date(from, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const sameYear = from.slice(0, 4) === to.slice(0, 4);
    return t("dashboard.range_label", {
        from: date(from, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) }),
        to: date(to, { day: "numeric", month: "short", year: "numeric" }),
    });
};

const PERIOD_BOOKMARK_KEY = "dashboard:period";
const TOUR_BOOKMARK_KEY = "dashboard:tour_seen";

const getTourSteps = (t) => [
    {
        target: "body",
        placement: "center",
        title: t("dashboard.tour.welcome_title"),
        content: t("dashboard.tour.welcome_content"),
    },
    {
        target: ".sidebar-nav",
        content: t("dashboard.tour.sidebar_content"),
        placement: "right",
    },
    {
        target: ".dash-period",
        content: t("dashboard.tour.period_content"),
    },
    {
        target: ".dash-refresh",
        content: t("dashboard.tour.refresh_content"),
    },
    {
        target: ".kpi-grid",
        content: t("dashboard.tour.kpis_content"),
    },
    {
        target: ".dcard--chart",
        content: t("dashboard.tour.chart_content"),
    },
    {
        target: ".dash-card--all-products",
        content: t("dashboard.tour.all_products_content"),
    },
    {
        target: ".tab-bar",
        content: t("dashboard.tour.activity_content"),
    },
];

/* ─── Dashboard ───────────────────────────────────── */
const Dashboard = () => {
    const { t } = useTranslation();
    const { setTitle, setTitleIcon, setTitleAvatar, setTitleDescription } = usePageTitle();
    const { user } = useAuth();
    const pharmacy = user?.pharmacy;
    const PERIODS = getPeriods(t);

    const [dashboard, setDashboard]           = useState(null);
    const [report, setReport]                 = useState(null);
    const [prevReport, setPrevReport]         = useState(null);
    const [period, setPeriod]                 = useState(30);
    const [customRange, setCustomRange]       = useState(null);
    const [customOpen, setCustomOpen]         = useState(false);
    const [customDraft, setCustomDraft]       = useState({ from: "", to: "" });
    const customRef                           = useRef(null);
    const [periodReady, setPeriodReady]       = useState(false);
    const [orders,    setOrders]              = useState([]);
    const [overduePatients, setOverduePatients] = useState([]);
    const [dashLoading, setDashLoading]       = useState(true);
    const [reportLoading, setReportLoading]   = useState(true);
    const [ordersLoading, setOrdersLoading]   = useState(true);
    const [overdueLoading, setOverdueLoading] = useState(true);
    const [activeTab, setActiveTab]           = useState("orders");
    const [metric, setMetric]                 = useState("revenue"); // charted in the overview card
    const [refreshing, setRefreshing]         = useState(false);
    const [prodPage, setProdPage]             = useState(0);
    const todoRef                             = useRef(null);
    const [lastUpdated, setLastUpdated]       = useState(new Date());
    const [now, setNow]                       = useState(Date.now());
    const [ordersPage, setOrdersPage]         = useState(0);
    const [overduePage, setOverduePage]       = useState(0);
    const [runTour, setRunTour]               = useState(false);

    useEffect(() => {
        setTitle(t('Dashboard'));
        document.title = t('Dashboard');
        setTitleIcon('ti-layout-dashboard');
        loadDashboard();
        loadOrders();
        loadOverduePatients();
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    // Restore the last period the user picked (persisted per-user on the backend).
    // The report isn't fetched until this resolves, so the initial load never
    // fires a request for the default period only to immediately discard it —
    // that race let the default-period response win and made the bookmark look
    // like it wasn't applied to the charts.
    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(PERIOD_BOOKMARK_KEY)}`, { withCredentials: true })
            .then(({ status, data }) => {
                const saved = status === 200 ? parsePeriodBookmark(data) : null;
                if (!saved) return;
                if (saved.customRange) setCustomRange(saved.customRange);
                setPeriod(saved.period);
            })
            .catch(() => {})
            .finally(() => setPeriodReady(true));
    }, []);

    useEffect(() => {
        if (!periodReady) return;
        loadReport();
        setProdPage(0);
    }, [period, customRange, periodReady]);

    // Close the custom-range popover on an outside click.
    useEffect(() => {
        if (!customOpen) return;
        const onDown = (e) => {
            if (customRef.current && !customRef.current.contains(e.target)) setCustomOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [customOpen]);

    // Onboarding tour — shown once per account, ever (persisted on the backend, same
    // mechanism as the period bookmark above), after the dashboard content it points to
    // has rendered. The "seen" flag is written as soon as we decide to run it (not on
    // finish/skip) so closing the tooltip early — the "X" only dismisses the current step
    // in react-joyride, it doesn't end the tour — or navigating away mid-tour still counts
    // as having shown it; it never comes back on a later visit.
    useEffect(() => {
        if (dashLoading) return;
        axios.get(`${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(TOUR_BOOKMARK_KEY)}`, { withCredentials: true })
            .then(({ status, data }) => {
                if (status === 200 && data === "1") return;
                setRunTour(true);
                axios.put(
                    `${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(TOUR_BOOKMARK_KEY)}`,
                    JSON.stringify("1"),
                    { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
                ).catch(() => {});
            })
            .catch(() => {});
    }, [dashLoading]);

    const handleTourCallback = ({ status }) => {
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            setRunTour(false);
        }
    };

    const savePeriodBookmark = (value) => {
        axios.put(
            `${API_BASE_URL}/api/v1/ui-bookmarks?key=${encodeURIComponent(PERIOD_BOOKMARK_KEY)}`,
            JSON.stringify(value),
            { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
        ).catch(() => {});
    };

    const handlePeriodChange = (days) => {
        setPeriod(days);
        setCustomOpen(false);
        savePeriodBookmark(days);
    };

    const openCustomPicker = () => {
        // prefill with the range currently shown, so tweaking one end is quick
        setCustomDraft(getPeriodRange(period, customRange));
        setCustomOpen(o => !o);
    };

    const applyCustomRange = () => {
        const { from, to } = customDraft;
        if (!from || !to || from > to) return;
        setCustomRange({ from, to });
        setPeriod("custom");
        setCustomOpen(false);
        savePeriodBookmark(`custom:${from}:${to}`);
    };

    // Subtitle shown on the KPI cards and card badges for the selected period.
    const periodLabel = period === "yesterday"
        ? t("dashboard.yesterday")
        : period === "custom" && customRange
            ? (customRange.from === customRange.to
                ? formatShortDate(customRange.from)
                : `${formatShortDate(customRange.from)} – ${formatShortDate(customRange.to)}`)
            // same wording as the selected period button ("1 mois", "7 jours"…)
            : (PERIODS.find((p) => p.days === period)?.label ?? t('dashboard.period_days', { count: period }));

    const loadDashboard = async () => {
        try {
            const { data } = await axios.get(`${API_BASE_URL}/api/dashboard`, { withCredentials: true });
            setDashboard(data);
        } catch (e) { console.error(e); }
        finally { setDashLoading(false); }
    };

    const loadReport = async () => {
        setReportLoading(true);
        try {
            const range = getPeriodRange(period, customRange);
            const fetchStats = (params) =>
                axios.get(`${API_BASE_URL}/api/v1/reports/stats`, { withCredentials: true, params }).then((r) => r.data);
            // previous period in parallel, for the KPI deltas — its failure mustn't hide the report
            const [data, prev] = await Promise.all([
                fetchStats(range),
                fetchStats(getPreviousRange(range)).catch(() => null),
            ]);
            setReport(data);
            setPrevReport(prev);
        } catch (e) { console.error(e); }
        finally { setReportLoading(false); }
    };

    const loadOrders = async () => {
        try {
            const { data } = await axios.get(`${API_BASE_URL}/api/v1/getOrders`, {
                withCredentials: true,
                params: { page: 0, size: 50, sortField: "id", sortDirection: "desc", status: "PENDING" },
            });
            setOrders(data.content || []);
            setOrdersPage(0);
        } catch (e) { console.error(e); }
        finally { setOrdersLoading(false); }
    };

    const loadOverduePatients = async () => {
        try {
            const { data } = await axios.get(`${API_BASE_URL}/api/v1/getPatients`, {
                withCredentials: true,
                params: { page: 0, size: 50, sortField: "id", sortDirection: "desc", isPaymentUpToDate: false },
            });
            setOverduePatients(data.content || []);
            setOverduePage(0);
        } catch (e) { console.error(e); }
        finally { setOverdueLoading(false); }
    };

    // credit card link: open the overdue patients tab and bring it into view
    const showOverdue = () => {
        setActiveTab("overdue");
        todoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([loadDashboard(), loadReport(), loadOrders(), loadOverduePatients()]);
        setLastUpdated(new Date());
        setRefreshing(false);
    };

    // Auto-refresh every 5 min, and a 30 s tick so "il y a N min" stays current. Through a ref:
    // the interval would otherwise keep the first render's handleRefresh (and its period).
    const refreshRef = useRef(handleRefresh);
    refreshRef.current = handleRefresh;
    useEffect(() => {
        const refresh = setInterval(() => refreshRef.current(), AUTO_REFRESH_MS);
        const tick = setInterval(() => setNow(Date.now()), 30 * 1000);
        return () => { clearInterval(refresh); clearInterval(tick); };
    }, []);

    // Pharmacy identity in the app's top bar (logo as the title avatar, name under "Tableau de
    // bord") — a second header row on the page itself repeated what the top bar already said.
    useEffect(() => {
        if (!pharmacy?.name) return;
        setTitleAvatar(
            <span className="dash-pharmacy__logo" style={{ background: stringToColor(pharmacy.name) }}>
                {nameInitials(pharmacy.name)}
            </span>
        );
        return () => setTitleAvatar(null);
    }, [pharmacy?.name, setTitleAvatar]);

    useEffect(() => {
        setTitleDescription(pharmacy?.name
            ? <span className="dash-pharmacy__name">{pharmacy.name}</span>
            : null);
    }, [pharmacy?.name, setTitleDescription]);

    useEffect(() => () => setTitleDescription(null), [setTitleDescription]);

    if (dashLoading) return <DashboardSkeleton />;
    if (!dashboard) {
        return (
            <div className="page-content dash">
                <ErrorState
                    title={t('dashboard.unable_to_load')}
                    message={t('dashboard.load_error_message')}
                    onRetry={handleRefresh}
                />
            </div>
        );
    }

    // KPI trends vs the same-length previous period (hidden while loading or with nothing to compare)
    const hasPrev = !reportLoading && report && prevReport;
    const revenueDelta  = hasPrev ? computeDelta(report.revenue, prevReport.revenue) : null;
    const basketDelta   = hasPrev ? computeDelta(report.avgBasket, prevReport.avgBasket) : null;
    const ordersDelta   = hasPrev ? computeDelta(report.ordersCount, prevReport.ordersCount) : null;

    // Stock urgency for the reorder list: out of stock, then less left than was sold, then ok.
    // Services (stock null) count as ok.
    const stockLevel = (p) => p.stock == null ? 'ok' : p.stock <= 0 ? 'out' : p.stock < p.qty ? 'low' : 'ok';
    const URGENCY = { out: 0, low: 1, ok: 2 };
    const allProds = (report?.allProductsSold || [])
        .map((p) => ({ ...p, unitPrice: p.qty > 0 ? p.revenue / p.qty : 0 }))
        // default order = most urgent first, then best sellers (column headers still re-sort)
        .sort((a, b) => URGENCY[stockLevel(a)] - URGENCY[stockLevel(b)] || b.qty - a.qty);
    // context lines of the Ventes / Panier moyen cards
    const itemsSold = allProds.reduce((sum, p) => sum + (Number(p.qty) || 0), 0);
    const itemsPerSale = report?.ordersCount > 0
        ? String(Math.round((itemsSold / report.ordersCount) * 10) / 10).replace('.', ',')
        : null;

    // Batches to deal with, each with its days left (negative = already expired)
    const todayStart = new Date(`${toISODate(new Date())}T00:00:00`);
    const expiringBatches = (dashboard.expiringBatches || []).map((b) => ({
        ...b,
        daysLeft: Math.round((new Date(`${b.expirationDate}T00:00:00`) - todayStart) / 86400000),
    }));

    // 5-per-page paging of the list
    const prodsTotalPages = Math.max(1, Math.ceil(allProds.length / PRODS_PAGE_SIZE));
    const prodsPageSafe = Math.min(prodPage, prodsTotalPages - 1);
    const prodsPageItems = allProds.slice(prodsPageSafe * PRODS_PAGE_SIZE, (prodsPageSafe + 1) * PRODS_PAGE_SIZE);
    const ordersTotalPages = Math.max(1, Math.ceil(orders.length / FEED_PAGE_SIZE));
    const ordersPageItems = orders.slice(ordersPage * FEED_PAGE_SIZE, ordersPage * FEED_PAGE_SIZE + FEED_PAGE_SIZE);
    const overdueTotalPages = Math.max(1, Math.ceil(overduePatients.length / FEED_PAGE_SIZE));
    const overduePageItems = overduePatients.slice(overduePage * FEED_PAGE_SIZE, overduePage * FEED_PAGE_SIZE + FEED_PAGE_SIZE);

    // One entry per day of the period with the three charted metrics, 0 on days without any —
    // the API only returns days that have payments / orders, which made the x axis skip dates
    const revenueByDay = new Map((report?.salesByDay || []).map((p) => [String(p.day).slice(0, 10), Number(p.total)]));
    const ordersByDay = new Map((report?.ordersByDay || []).map((p) => [String(p.status).slice(0, 10), Number(p.count)]));
    const periodRange = getPeriodRange(period, customRange);
    const todayISO = toISODate(new Date());
    const days = [];
    for (let d = new Date(`${periodRange.from}T00:00:00`), end = new Date(`${periodRange.to}T00:00:00`); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = toISODate(d);
        const day = d.toLocaleDateString("fr-MA", { day: "numeric", month: "short" });
        const revenue = revenueByDay.get(iso) ?? 0;
        const count = ordersByDay.get(iso) ?? 0;
        // today is still in progress: lighter bar, said so in the tooltip
        const isToday = iso === todayISO;
        days.push({
            day,
            revenue,
            orders: count,
            basket: count > 0 ? revenue / count : 0,
            highlight: isToday,
            tooltipLabel: isToday ? `${day} · ${t('dashboard.today')}` : undefined,
        });
    }
    // which figure the chart shows, next to its title
    const METRIC_LABELS = {
        revenue: t('dashboard.revenue'),
        orders: t('Orders'),
        basket: t('dashboard.avg_basket'),
    };
    const CHART_FORMAT = {
        revenue: (v) => `${fmt(v)} dhs`,
        orders: (v) => t('dashboard.sales_count', { count: v }),
        basket: (v) => `${fmt(v)} dhs`,
    };
    const chartData = days.map((d) => ({ day: d.day, total: d[metric], highlight: d.highlight, tooltipLabel: d.tooltipLabel }));

    return (
        <div className="page-content dash">

            <Joyride
                run={runTour}
                steps={getTourSteps(t)}
                continuous
                showSkipButton
                scrollToFirstStep
                callback={handleTourCallback}
                locale={{
                    back: t('Previous'),
                    close: t('Close'),
                    last: t('dashboard.tour.last'),
                    next: t('Next'),
                    skip: t('dashboard.tour.skip'),
                }}
                options={{
                    primaryColor: '#2563eb',
                    textColor: '#334155',
                    backgroundColor: '#ffffff',
                    overlayColor: 'rgba(15, 23, 42, 0.55)',
                    arrowColor: '#ffffff',
                    spotlightPadding: 8,
                    spotlightRadius: 10,
                    width: 360,
                    zIndex: 10000,
                }}
                styles={{
                    spotlight: { borderRadius: 10 },
                    tooltip: {
                        borderRadius: 14,
                        padding: '22px 24px',
                        boxShadow: '0 20px 45px rgba(15, 23, 42, 0.18)',
                    },
                    tooltipContainer: { textAlign: 'left' },
                    tooltipTitle: {
                        fontSize: 17,
                        fontWeight: 700,
                        color: '#0f172a',
                        marginBottom: 6,
                    },
                    tooltipContent: {
                        fontSize: 14,
                        lineHeight: 1.6,
                        padding: '8px 0 4px',
                    },
                    tooltipFooter: { marginTop: 18 },
                    buttonPrimary: {
                        borderRadius: 999,
                        padding: '10px 22px',
                        fontSize: 14,
                        fontWeight: 700,
                    },
                    buttonBack: {
                        fontSize: 14,
                        fontWeight: 600,
                        marginRight: 12,
                    },
                    buttonSkip: { fontSize: 13 },
                    buttonClose: {
                        color: '#374151',
                        width: 10,
                        height: 10,
                        top: 12,
                        right: 12,
                        padding: 6,
                        borderRadius: 7,
                        backgroundColor: '#f3f4f6',
                        lineHeight: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                }}
            />

            {/* ── Toolbar ── */}
            {/* dates of the period + period controls — the pharmacy identity and refresh status
                are in the top bar */}
            <div className="dash-toolbar">
                <div className="dash-toolbar__left">
                    <span className="dash-range">
                        <FontAwesomeIcon icon={faCalendarDays} />
                        <span className="dash-range__text">{formatRangeLabel(periodRange, t)}</span>
                    </span>
                    {/* relative age (the data auto-refreshes every 5 min); exact time in the tooltip */}
                    {(() => {
                        const time = lastUpdated.toLocaleTimeString("fr-MA", { hour: "2-digit", minute: "2-digit" });
                        const minutes = Math.floor((now - lastUpdated.getTime()) / 60000);
                        return (
                            <span className="dash-status" title={t('dashboard.data_refreshed_at', { time })}>
                                <span className={`dash-status__dot ${refreshing ? "is-busy" : ""}`} />
                                {minutes < 1 ? t('dashboard.refreshed_just_now')
                                    : minutes < 60 ? t('dashboard.refreshed_minutes_ago', { count: minutes })
                                    : t('dashboard.data_refreshed_at', { time })}
                            </span>
                        );
                    })()}
                </div>
                <div className="dash-period-wrap" ref={customRef}>
                    <div className="dash-period">
                        {/* all periods, custom range included, as one segmented control on a gray track */}
                        <div className="dash-period__track">
                            {PERIODS.map((p) => (
                                <button
                                    key={p.days}
                                    className={`dash-period__btn ${period === p.days ? "active" : ""}`}
                                    onClick={() => handlePeriodChange(p.days)}
                                >
                                    {p.label}
                                </button>
                            ))}
                            <button
                                // icon only ("Personnaliser" in the tooltip); once a custom range is
                                // applied its dates show next to the icon
                                className={`dash-period__btn dash-period__btn--custom ${period === "custom" && customRange ? "has-range" : ""} ${period === "custom" ? "active" : ""}`}
                                onClick={openCustomPicker}
                                aria-label={t("dashboard.custom_period")}
                                data-tooltip-id="tooltip"
                                data-tooltip-content={t("dashboard.custom_period")}
                            >
                                <FontAwesomeIcon icon={faCalendarDays} />
                                {period === "custom" && customRange && periodLabel}
                            </button>
                        </div>
                        {/* refresh, at the end of the controls */}
                        <button
                            type="button"
                            className="dash-refresh"
                            onClick={handleRefresh}
                            disabled={refreshing}
                        >
                            <FontAwesomeIcon icon={faArrowsRotate} className={refreshing ? "spin" : ""} />
                            {t('dashboard.refresh')}
                        </button>
                    </div>
                    {customOpen && (
                        <div className="dash-custom-range">
                            <label>
                                <span>{t("dashboard.custom_from")}</span>
                                <input
                                    type="date"
                                    value={customDraft.from}
                                    max={customDraft.to || toISODate(new Date())}
                                    onChange={e => setCustomDraft(d => ({ ...d, from: e.target.value }))}
                                />
                            </label>
                            <label>
                                <span>{t("dashboard.custom_to")}</span>
                                <input
                                    type="date"
                                    value={customDraft.to}
                                    min={customDraft.from || undefined}
                                    max={toISODate(new Date())}
                                    onChange={e => setCustomDraft(d => ({ ...d, to: e.target.value }))}
                                />
                            </label>
                            <button
                                className="dash-custom-range__apply"
                                onClick={applyCustomRange}
                                disabled={!customDraft.from || !customDraft.to || customDraft.from > customDraft.to}
                            >
                                {t("dashboard.custom_apply")}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── KPI cards: the three period figures select what the chart shows ── */}
            <div className="kpi-grid">
                <KpiCard
                    t={t}
                    label={t('dashboard.revenue')}
                    loading={reportLoading}
                    value={fmt(report?.revenue)}
                    unit="dhs"
                    delta={revenueDelta}
                    sub={revenueDelta ? t('dashboard.vs_previous') : periodLabel}
                    spark={days.map((d) => d.revenue)}
                    active={metric === 'revenue'}
                    onSelect={() => setMetric('revenue')}
                />
                <KpiCard
                    t={t}
                    label={t('Orders')}
                    loading={reportLoading}
                    value={report?.ordersCount ?? 0}
                    delta={ordersDelta}
                    sub={t('dashboard.items_sold', { count: itemsSold })}
                    spark={days.map((d) => d.orders)}
                    active={metric === 'orders'}
                    onSelect={() => setMetric('orders')}
                />
                <KpiCard
                    t={t}
                    label={t('dashboard.avg_basket')}
                    loading={reportLoading}
                    value={fmt(report?.avgBasket)}
                    unit="dhs"
                    delta={basketDelta}
                    sub={itemsPerSale && t('dashboard.items_per_sale', { value: itemsPerSale })}
                    spark={days.map((d) => d.basket)}
                    active={metric === 'basket'}
                    onSelect={() => setMetric('basket')}
                />
                {/* current state, not a period figure: no sparkline, a shortcut to the patients instead */}
                <KpiCard
                    t={t}
                    label={t('dashboard.credit_sales')}
                    value={fmt(dashboard.creditSalesTotal)}
                    unit="dhs"
                    tag={t('dashboard.current')}
                    sub={t('dashboard.credit_sales_sub')}
                    link={(
                        <button type="button" className="kpi-card__link" onClick={showOverdue}>
                            <span>{t('dashboard.hero_sub', { count: overduePatients.length })}</span>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                    )}
                />
            </div>

            {/* ── Sales evolution | To do — same height, side by side ── */}
            <div className="dash-row">
                <section className="dcard dcard--chart">
                    <header className="dcard__head">
                        <h2 className="dcard__title">{t('dashboard.sales_evolution')}</h2>
                        <span className="dcard__meta">{METRIC_LABELS[metric]}</span>
                    </header>
                    <div className="dcard__chart">
                        <SalesTrendChart data={chartData} valueFormatter={CHART_FORMAT[metric]} />
                    </div>
                </section>

                {/* Everything that needs an action, in one place: one tab per kind (the tabs are the header) */}
                <section className="dcard dash-card--activity" ref={todoRef}>
                    <div className="tab-bar" role="tablist" aria-label={t('dashboard.todo')}>
                        <button className={`tab-btn ${activeTab === "orders" ? "active" : ""}`}
                            onClick={() => setActiveTab("orders")}>
                            <span className="tab-btn__label" title={t('dashboard.pending_orders')}>{t('dashboard.tab_pending')}</span>
                            {orders.length > 0 && <span className="tab-count">{orders.length}</span>}
                        </button>
                        <button className={`tab-btn ${activeTab === "overdue" ? "active" : ""}`}
                            onClick={() => setActiveTab("overdue")}>
                            <span className="tab-btn__label" title={t('dashboard.overdue_patients')}>{t('dashboard.tab_unpaid')}</span>
                            {overduePatients.length > 0 && <span className="tab-count">{overduePatients.length}</span>}
                        </button>
                        <button className={`tab-btn ${activeTab === "expiring" ? "active" : ""}`}
                            onClick={() => setActiveTab("expiring")}>
                            <span className="tab-btn__label" title={t('dashboard.expiring_tab')}>{t('dashboard.expiring_tab')}</span>
                            {dashboard.expiringBatchesCount > 0 && <span className="tab-count">{dashboard.expiringBatchesCount}</span>}
                        </button>
                    </div>
                    <div className="dash-panel__body">

                    {activeTab === "orders" && (
                        <div className="feed">
                            {ordersLoading ? <FeedSkeleton />
                            : orders.length === 0 ? <div className="feed-empty">{t('dashboard.no_pending_orders')}</div>
                            : (
                                <>
                                    {ordersPageItems.map((o) => (
                                        <div key={o.id} className="feed-row">
                                            {/* patient's initials; the order icon for a walk-in visitor */}
                                            {o.patient ? (
                                                <div className="feed-icon feed-icon--avatar" style={avatarStyle(`${o.patient.firstName} ${o.patient.lastName}`)}>
                                                    {nameInitials(`${o.patient.firstName} ${o.patient.lastName}`)}
                                                </div>
                                            ) : (
                                                <div className="feed-icon">
                                                    <FontAwesomeIcon icon={faClipboardList} />
                                                </div>
                                            )}
                                            <div className="feed-body">
                                                <NavLink to={`/order/${o.id}`} className="feed-name">{o.reference}</NavLink>
                                                <span className="feed-sub">
                                                    {o.patient ? `${o.patient.firstName} ${o.patient.lastName}` : t('Visitor')}
                                                </span>
                                            </div>
                                            <div className="feed-right">
                                                <span className="feed-amount">{fmt(o.total)} <span className="feed-unit">dhs</span></span>
                                                <Pill status={o.status || "PENDING"} />
                                            </div>
                                        </div>
                                    ))}
                                    {ordersTotalPages > 1 && (
                                        <div className="all-prods-pager">
                                            <button
                                                type="button"
                                                className="all-prods-pager__btn"
                                                onClick={() => setOrdersPage((p) => Math.max(0, p - 1))}
                                                disabled={ordersPage === 0}
                                            >
                                                {t('dashboard.prev_page')}
                                            </button>
                                            <span className="all-prods-pager__info">
                                                {t('dashboard.page_of', { page: ordersPage + 1, total: ordersTotalPages })}
                                            </span>
                                            <button
                                                type="button"
                                                className="all-prods-pager__btn"
                                                onClick={() => setOrdersPage((p) => Math.min(ordersTotalPages - 1, p + 1))}
                                                disabled={ordersPage >= ordersTotalPages - 1}
                                            >
                                                {t('dashboard.next_page')}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === "overdue" && (
                        <div className="feed">
                            {overdueLoading ? <FeedSkeleton />
                            : overduePatients.length === 0 ? <div className="feed-empty">{t('dashboard.no_overdue_patients')}</div>
                            : (
                                <>
                                    {overduePageItems.map((p) => (
                                        <div key={p.id} className="feed-row">
                                            <div className="feed-icon feed-icon--avatar" style={avatarStyle(`${p.firstName} ${p.lastName}`)}>
                                                {nameInitials(`${p.firstName} ${p.lastName}`)}
                                            </div>
                                            <div className="feed-body">
                                                <NavLink to={`/patient/${p.id}`} className="feed-name">
                                                    {p.firstName} {p.lastName}
                                                </NavLink>
                                                <span className="feed-sub">{p.phoneNumber || t('dashboard.no_phone')}</span>
                                            </div>
                                            <div className="feed-right">
                                                <span className="feed-amount">{fmt(p.remainingToPay)} <span className="feed-unit">dhs</span></span>
                                            </div>
                                        </div>
                                    ))}
                                    {overdueTotalPages > 1 && (
                                        <div className="all-prods-pager">
                                            <button
                                                type="button"
                                                className="all-prods-pager__btn"
                                                onClick={() => setOverduePage((p) => Math.max(0, p - 1))}
                                                disabled={overduePage === 0}
                                            >
                                                {t('dashboard.prev_page')}
                                            </button>
                                            <span className="all-prods-pager__info">
                                                {t('dashboard.page_of', { page: overduePage + 1, total: overdueTotalPages })}
                                            </span>
                                            <button
                                                type="button"
                                                className="all-prods-pager__btn"
                                                onClick={() => setOverduePage((p) => Math.min(overdueTotalPages - 1, p + 1))}
                                                disabled={overduePage >= overdueTotalPages - 1}
                                            >
                                                {t('dashboard.next_page')}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* batches in stock expired or within the pharmacy's warning window, soonest first */}
                    {activeTab === "expiring" && (
                        <div className="feed">
                            {expiringBatches.length === 0 ? (
                                <div className="feed-empty feed-empty--ok">
                                    <FontAwesomeIcon icon={faCircleCheck} />
                                    {t('dashboard.no_expiring')}
                                </div>
                            ) : (
                                <>
                                    {expiringBatches.map((b, i) => (
                                        <div key={`${b.productId}-${b.batchNumber}-${i}`} className="feed-row">
                                            <div className={`feed-icon feed-icon--${b.daysLeft < 0 ? 'danger' : 'warning'}`}>
                                                <FontAwesomeIcon icon={faHourglassHalf} />
                                            </div>
                                            <div className="feed-body">
                                                <NavLink to={`/products/${b.productId}`} className="feed-name">{b.productName}</NavLink>
                                                <span className="feed-sub">
                                                    {b.batchNumber && `${t('dashboard.lot_label', { number: b.batchNumber })} · `}
                                                    {t('dashboard.units', { count: b.quantity })}
                                                </span>
                                            </div>
                                            <div className="feed-right">
                                                <span className="feed-amount">{formatShortDate(b.expirationDate)}</span>
                                                <span className={`status-pill ${b.daysLeft < 0 ? 'pill-red' : b.daysLeft <= 30 ? 'pill-amber' : 'pill-neutral'}`}>
                                                    {b.daysLeft < 0 ? t('dashboard.expired')
                                                        : b.daysLeft === 0 ? t('dashboard.today')
                                                        : t('dashboard.expires_in_days', { count: b.daysLeft })}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    {dashboard.expiringBatchesCount > expiringBatches.length && (
                                        <div className="feed-more">
                                            {t('dashboard.more_batches', { count: dashboard.expiringBatchesCount - expiringBatches.length })}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                    </div>
                </section>
            </div>

            {/* ── All products sold over the period — the reorder list, most urgent stock first ── */}
            <section className="dcard dash-card--all-products">
                <header className="dcard__head">
                    <h2 className="dcard__title">{t('dashboard.all_products_sold')}</h2>
                    {!reportLoading && allProds.length > 0 && (
                        <span className="dcard__meta">{t('dashboard.products_intro', { count: allProds.length, period: periodLabel })}</span>
                    )}
                </header>
                {!reportLoading && allProds.length === 0 ? (
                    // nothing sold over the period: an illustrated empty state instead of an empty list
                    <div className="dash-empty">
                        <div className="dash-empty__art">
                            <span className="dash-empty__ring" />
                            <span className="dash-empty__icon">
                                <FontAwesomeIcon icon={faBoxOpen} />
                            </span>
                        </div>
                        <h3 className="dash-empty__title">{t('dashboard.no_products')}</h3>
                        <p className="dash-empty__message">{t('dashboard.no_products_sold_hint')}</p>
                    </div>
                ) : (
                <div className="dash-panel__body">
                    {reportLoading ? <FeedSkeleton rows={5} /> : (
                        <>
                            <div className="prods-head">
                                <span>{t('Product')}</span>
                                <span>{t('dashboard.qty_sold')}</span>
                                <span>{t('dashboard.stock')}</span>
                                <span>{t('dashboard.unit_price')}</span>
                            </div>
                            <div className="prods-list">
                            {prodsPageItems.length === 0 ? (
                                <div className="feed-empty">{t('dashboard.no_products')}</div>
                            ) : prodsPageItems.map((p) => {
                                // stock coloured as a reorder hint: out of stock, or less left than was
                                // sold over the period → won't last; null for services (no stock)
                                const level = stockLevel(p);
                                const sub = [p.presentation, p.dosage].filter(Boolean).join(' · ');
                                return (
                                    <div key={p.id} className="prods-row">
                                        <div className="prods-row__product">
                                            <img src={p.image || '/images/no_product.png'} alt="" />
                                            <div className="prods-row__text">
                                                <NavLink to={`/products/${p.id}`} className="prods-row__name" title={p.name}>{p.name}</NavLink>
                                                {sub && <span className="prods-row__sub" title={sub}>{sub}</span>}
                                            </div>
                                        </div>
                                        <span className="prods-row__cell"><span className="all-prods-tag">{p.qty}</span></span>
                                        <span className="prods-row__cell">
                                            {p.stock == null ? <span className="cell-muted">—</span> : (
                                                <span
                                                    className={`all-prods-stock all-prods-stock--${level}`}
                                                    title={level === 'out' ? t('product.out_of_stock') : level === 'low' ? t('product.low_stock') : undefined}
                                                >
                                                    <span className="all-prods-stock__dot" />
                                                    {p.stock}
                                                </span>
                                            )}
                                        </span>
                                        <span className="prods-row__cell prods-row__price">
                                            {fmt(p.unitPrice)} <span className="all-prods-unit">dhs</span>
                                        </span>
                                    </div>
                                );
                            })}
                            </div>
                            {prodsTotalPages > 1 && (
                                <div className="all-prods-pager">
                                    <button
                                        type="button"
                                        className="all-prods-pager__btn"
                                        onClick={() => setProdPage(Math.max(0, prodsPageSafe - 1))}
                                        disabled={prodsPageSafe === 0}
                                    >
                                        {t('dashboard.prev_page')}
                                    </button>
                                    <span className="all-prods-pager__info">
                                        {t('dashboard.page_of', { page: prodsPageSafe + 1, total: prodsTotalPages })}
                                    </span>
                                    <button
                                        type="button"
                                        className="all-prods-pager__btn"
                                        onClick={() => setProdPage(Math.min(prodsTotalPages - 1, prodsPageSafe + 1))}
                                        disabled={prodsPageSafe >= prodsTotalPages - 1}
                                    >
                                        {t('dashboard.next_page')}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
                )}
            </section>
        </div>
    );
};

export default Dashboard;
